import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import Tooltip from "@mui/material/Tooltip";
import ElipsisCell from "../../../../../components/TableBox/Cells/EllipsisCell";
import ElipsisText from "../../../../../components/ElipsisText";
import Checkbox from "../../../../../components/Checkbox";
import Badge from "../../../../../components/Badge";
import Link from "../../../../../components/Link";
import { RowClassName, Modules, RouteParamsTypes } from "../../../../../enums";
import { TableContext } from "../../../../../contexts/TableContext";
import { VALIDATION_FIELD_MAPPING } from "../../../../../utils/appConstant";
import { formatAmount, formatDollarAmount, getDisplayedMarginAmount } from "../../../../../utils/methods";
import { TransactionListItem } from "../../../../../interfaces/managerStatement.interface";

interface TransactionsRowProps {
  item: TransactionListItem;
  className?: RowClassName;
  selectedIds: number[];
  onSelectToggle: (transactionId: number) => void;
  onAdjustClick: (item: TransactionListItem) => void;
  onCalcClick: (item: TransactionListItem) => void;
  readOnly?: boolean;
  saving?: boolean;
}

const TransactionsRow: React.FC<TransactionsRowProps> = ({
  item, className, selectedIds, onSelectToggle, onAdjustClick, onCalcClick, readOnly, saving,
}) => {
  const { t } = useTranslation();
  const { headCells } = useContext(TableContext);

  const getStatusVariant = (status: string) => {
    if (status === VALIDATION_FIELD_MAPPING.unBilled) return "secondary";
    else if (status === VALIDATION_FIELD_MAPPING.invoice) return "warning";
    else return "info";
  };

  // Deliberately disjoint from getStatusVariant's palette (secondary/warning/info) so the
  // Statement Status badge is never mistaken for the Invoice Status badge in the same row.
  const getStatementStatusVariant = (status: string) => {
    if (status === 'pending') return "primary";
    else if (status === 'settled') return "success";
    else return "danger"; // generated
  };

  const isSelected = selectedIds.includes(item.transaction_id);
  const rowStateClassName = item.needs_review
    ? 'bg-warning-subtle'
    : (item.is_manually_adjusted ? 'bg-primary-subtle' : '');

  return (
    <tr className={clsx(className, rowStateClassName)}>
      {headCells.map((headCell) => {
        if (headCell.id === VALIDATION_FIELD_MAPPING.select) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <Checkbox
                variant="primary"
                className="d-checkbox mb-0"
                checked={isSelected}
                disabled={readOnly}
                name={VALIDATION_FIELD_MAPPING.select}
                onChange={() => onSelectToggle(item.transaction_id)}
              />
            </td>
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.date) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={item.date || '-'}
              node={item.date ? (
                <Link
                  text={item.date}
                  href={`${Modules.transactions}${RouteParamsTypes.view}/${item.transaction_id}`}
                  target="_blank"
                />
              ) : undefined}
              className={item.date ? 'h6 mb-1 text-decoration-underline hyperlink' : undefined}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.propertyName) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={<ElipsisText width="100%" text={item.property_name || '-'} />}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.expenseCategory) {
          return <ElipsisCell key={headCell.id} width={headCell.width} text={item.expense_category_label || '-'} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.paidBy) {
          return <ElipsisCell key={headCell.id} width={headCell.width} text={item.paid_by_label || '-'} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.transactionType) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <div className="d-flex align-items-center gap-1">
                <ElipsisText width="100%" text={item.transaction_type_name || '-'} />
                {item.transaction_details && (
                  <Tooltip title={item.transaction_details} arrow classes={{ tooltip: 'tooltip' }}>
                    <i
                      className="ri-information-line ri-16px text-muted"
                      aria-label={t('reconciliation.managerStatement.detail.pageTexts.detailsTab.transactions.columns.transactionType')}
                    />
                  </Tooltip>
                )}
              </div>
              {item.chargeable === false && (
                <ElipsisText text="Non chargeable" width="100%" className="fst-italic text-muted" />
              )}
            </td>
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.status) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <Badge
                text={item.transaction_status_label || '-'}
                variant={getStatusVariant(item.transaction_status)}
              />
            </td>
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.statementStatus) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <Badge text={item.statement_status_label || '-'} variant={getStatementStatusVariant(item.statement_status)} />
            </td>
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.actualAmount) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <div className="fw-semibold">{formatDollarAmount(formatAmount(Number(item.actual_amount || 0).toFixed(2)))}</div>
              {item.adjust_against_claim && (
                <ElipsisText text={t('reconciliation.managerStatement.detail.pageTexts.detailsTab.transactions.claimCaption')} width="100%" className="fst-italic text-muted" />
              )}
              {item.adjust_against_claim && !item.claim_settled && (
                <ElipsisText text="Claim not settled" width="100%" className="fst-italic text-muted" />
              )}
            </td>
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.marginAmount) {
          const marginAmount = getDisplayedMarginAmount(item);
          return (
            <td key={headCell.id} width={headCell.width}>
              <div className="fw-semibold">{formatDollarAmount(formatAmount(marginAmount.toFixed(2)))}</div>
            </td>
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.paidToManager) {
          const paidToManager = Number(item.paid_to_manager || 0);
          const paidToManagerText = formatDollarAmount(formatAmount(paidToManager.toFixed(2)));
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={paidToManagerText}
              node={(
                <Link
                  text={paidToManagerText}
                  className={clsx('text-decoration-underline', { 'text-danger': paidToManager < 0 })}
                  onClick={() => onCalcClick(item)}
                />
              )}
              className="fw-semibold hyperlink"
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.transactionAdjustAction) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <Tooltip
                title={t('reconciliation.managerStatement.detail.pageTexts.detailsTab.transactions.adjustTooltip')}
                arrow
                classes={{ tooltip: 'tooltip' }}
              >
                <span>
                  <button
                    type="button"
                    className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
                    aria-label={t('reconciliation.managerStatement.detail.pageTexts.detailsTab.transactions.adjustTooltip')}
                    disabled={!isSelected || saving}
                    onClick={() => onAdjustClick(item)}
                  >
                    <i className="ri-edit-box-line ri-20px" />
                  </button>
                </span>
              </Tooltip>
            </td>
          );
        }

        return <ElipsisCell key={headCell.id} width={headCell.width} text="-" />;
      })}
    </tr>
  );
};

export default TransactionsRow;
